// Sidebar lateral fiel ao design do dashboards principal.
// Brand no topo + grupos colapsáveis + indicador active com barra esquerda.

import { useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";

export interface SidebarItem {
  id: string;
  label: string;
  icon: ReactNode;
  to: string;
  count?: number;
}
export interface SidebarGroup {
  label: string;
  items: SidebarItem[];
}

export interface Props {
  groups: SidebarGroup[];
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export default function Sidebar({ groups, mobileOpen = false, onMobileClose }: Props) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const toggle = (label: string) => setCollapsedGroups((p) => { const n = new Set(p); n.has(label) ? n.delete(label) : n.add(label); return n; });

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-esm-black/40 backdrop-blur-sm lg:hidden" onClick={onMobileClose} />
      )}
      <aside
        className={`shrink-0 z-50 bg-white border-r border-esm-gray-100 flex flex-col h-screen sticky top-0 w-64
          max-lg:fixed max-lg:top-0 max-lg:left-0 max-lg:h-dvh max-lg:shadow-lg max-lg:transition-transform
          ${mobileOpen ? "max-lg:translate-x-0" : "max-lg:-translate-x-full"}`}
      >
        <div className="h-16 flex items-center px-4 border-b border-esm-gray-100 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-esm-brown-500 to-esm-brown-900 flex items-center justify-center text-white shadow-sm">
              <svg width="18" height="18" viewBox="0 0 24 14" fill="none">
                <path d="M21 6C16 1 9 -3 4 4 3 5 2 6 1 6c5 4 8-1 11-2 3-1 6 2 9 4 3 2 6 1 6 1-3 0-6-2-6-3z" fill="currentColor"/>
              </svg>
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-bold text-esm-gray-900 leading-tight truncate">EPH Ads Pipeline</div>
              <div className="text-[10px] text-esm-gray-500 truncate">Agente Claude</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-2.5 py-3">
          {groups.map((g, gi) => {
            const open = !collapsedGroups.has(g.label);
            return (
              <div key={g.label} className={gi > 0 ? "mt-4" : ""}>
                <button
                  type="button"
                  onClick={() => toggle(g.label)}
                  className="w-full flex items-center justify-between gap-2 px-2.5 mb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-esm-gray-400 hover:text-esm-gray-700 transition-colors"
                >
                  <span>{g.label}</span>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className={`transition-transform shrink-0 ${open ? "rotate-0" : "-rotate-90"}`}>
                    <path d="M2.5 4L5 6.5L7.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {open && (
                  <ul className="flex flex-col gap-0.5">
                    {g.items.map((it) => (
                      <li key={it.id}>
                        <NavLink
                          to={it.to}
                          onClick={onMobileClose}
                          className={({ isActive }) =>
                            `group relative w-full flex items-center gap-2.5 rounded-xl h-9 px-2.5 transition-colors
                            ${isActive ? "bg-esm-brown-50 text-esm-gray-900" : "text-esm-gray-600 hover:bg-esm-gray-50 hover:text-esm-gray-900"}`
                          }
                        >
                          {({ isActive }) => (
                            <>
                              <span className={`shrink-0 ${isActive ? "text-esm-gray-900" : "text-esm-gray-500 group-hover:text-esm-gray-900"}`}>
                                {it.icon}
                              </span>
                              <span className={`flex-1 text-left text-[13px] truncate ${isActive ? "font-bold" : "font-medium"}`}>
                                {it.label}
                              </span>
                              {typeof it.count === "number" && it.count > 0 && (
                                <span className={`inline-flex items-center justify-center rounded-full px-1.5 min-w-[1.25rem] h-5 text-[10px] font-bold tabular-nums shrink-0 ${isActive ? "bg-esm-brown-500 text-white" : "bg-esm-gray-100 text-esm-gray-600"}`}>
                                  {it.count}
                                </span>
                              )}
                            </>
                          )}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-esm-gray-100 p-2.5">
          <div className="flex items-center gap-2.5 rounded-lg p-2 hover:bg-esm-gray-50 transition-colors">
            <span className="w-8 h-8 rounded-full bg-esm-brown-100 text-esm-brown-700 text-[11px] font-bold flex items-center justify-center shrink-0">
              EPH
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-bold text-esm-gray-900 truncate leading-tight">Esmeralda Praia Hotel</div>
              <div className="text-[10px] text-esm-gray-500 truncate">modo suggest_only</div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
